/**
 * Referência de um card à sua memória de cálculo (`GET /fleet-memory/:cardKey`).
 *
 * `from`/`to` e `params` são EXATAMENTE os que a tela mandou para o endpoint do
 * card — a memória refaz a conta no mesmo recorte. `expected` é o número cru que
 * o card exibe: a gaveta compara e avisa quando a memória não fecha com ele.
 */
import type { FleetMemoryUnit } from "@bi/types";

export interface CardMemoryRef {
  cardKey: string;
  from?: string;
  to?: string;
  params?: Record<string, string | null | undefined>;
  expected?: number | null;
  /** título provisório enquanto carrega */
  title?: string;
}

export function memoryQueryString(m: CardMemoryRef, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams();
  if (m.from) q.set("from", m.from);
  if (m.to) q.set("to", m.to);
  for (const [k, v] of Object.entries(m.params ?? {})) if (v != null && v !== "") q.set(k, v);
  for (const [k, v] of Object.entries(extra)) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : "";
}

/**
 * O número da memória fecha com o do card? Contagem: igual. Dinheiro: até um
 * centavo (o card soma em ponto flutuante, a memória arredonda). Razões e
 * percentuais: diferença relativa desprezível.
 */
export function valuesMatch(unit: FleetMemoryUnit, memoryValue: number, cardValue: number): boolean {
  const diff = Math.abs(memoryValue - cardValue);
  if (unit === "count") return diff < 0.5;
  if (unit === "BRL") return diff <= 0.011 + Math.abs(cardValue) * 1e-9;
  return diff <= Math.max(Math.abs(cardValue) * 1e-4, 1e-6);
}

/**
 * Chaves da URL da tela de memória que NÃO vão para o endpoint: o número do
 * card (para conferir) e o título provisório. O resto (from/to + params) é o
 * recorte e segue para `/fleet-memory` como veio.
 */
const PAGE_ONLY_KEYS = new Set(["expected", "title"]);

/** Rota da tela de memória de um card, levando recorte, número do card e título. */
export function memoryPagePath(m: CardMemoryRef, base = "/frota/memoria"): string {
  const extra: Record<string, string> = {};
  if (m.expected != null && Number.isFinite(m.expected)) extra.expected = String(m.expected);
  if (m.title) extra.title = m.title;
  return `${base}/${encodeURIComponent(m.cardKey)}${memoryQueryString(m, extra)}`;
}

/** Inverso de `memoryPagePath`: remonta a referência a partir da URL da tela. */
export function memoryRefFromSearch(cardKey: string, search: URLSearchParams): CardMemoryRef {
  const params: Record<string, string> = {};
  for (const [k, v] of search) if (k !== "from" && k !== "to" && !PAGE_ONLY_KEYS.has(k)) params[k] = v;
  const exp = search.get("expected");
  return {
    cardKey,
    from: search.get("from") ?? undefined,
    to: search.get("to") ?? undefined,
    params,
    expected: exp != null && exp !== "" && Number.isFinite(Number(exp)) ? Number(exp) : undefined,
    title: search.get("title") ?? undefined,
  };
}
